import { wrap, releaseProxy } from "comlink";

class WorkerManager {
  constructor() {
    // Data processing workers
    this.dataParseWorker = null;
    this.fieldSettingsWorker = null;
    this.dataParseWorkerInstance = null;
    this.fieldSettingsWorkerInstance = null;
    
    // Formula and graph workers (for dashboard and form calculations)
    this.formulaWorker = null;
    this.graphWorker = null;
    this.formulaWorkerInstance = null;
    this.graphWorkerInstance = null;
    
    this.refCount = 0;
    this.isInitializing = false;
    this.initPromise = null;
    this.terminateTimeout = null;
    
    // Track which workers are needed by which pages
    this.pageWorkerRequirements = new Map();
    this.currentPage = null;
  }

  async initialize() {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.dataParseWorker && this.fieldSettingsWorker && this.formulaWorker && this.graphWorker) {
      this.refCount++;
      return;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  async initializeSelective(requiredWorkers) {
    if (this.initPromise) {
      return this.initPromise;
    }

    // Check if all required workers already exist
    const allRequiredExist = requiredWorkers.every(workerType => {
      switch(workerType) {
        case 'dataParseWorker': return this.dataParseWorker;
        case 'fieldSettingsWorker': return this.fieldSettingsWorker;
        case 'formulaWorker': return this.formulaWorker;
        case 'graphWorker': return this.graphWorker;
        default: return false;
      }
    });

    if (allRequiredExist) {
      this.refCount++;
      return;
    }

    this.initPromise = this._doInitializeSelective(requiredWorkers);
    return this.initPromise;
  }

  async _doInitialize() {
    try {
      console.log('🔧 Initializing shared workers...');
      console.log('📁 Loading all worker types: dataParse, fieldSettings, formula, graph');
      
      // Import all worker classes dynamically
      const [
        DataParseWorkerFile,
        FieldSettingsWorkerFile,
        FormulaWorkerFile,
        GraphWorkerFile
      ] = await Promise.all([
        import("@/workers/dataParse.worker.js").then(m => m.default),
        import("@/workers/fieldSettings.worker.js").then(m => m.default),
        import("@/workers/formula.worker.js").then(m => m.default),
        import("@/workers/graph.worker.js").then(m => m.default)
      ]);
      
      // Create all worker instances
      console.log('🏗️ Creating DataParseWorker instance (dataParse.worker.js)');
      this.dataParseWorkerInstance = new DataParseWorkerFile();
      this.dataParseWorker = wrap(this.dataParseWorkerInstance);
      
      console.log('🏗️ Creating FieldSettingsWorker instance (fieldSettings.worker.js)');
      this.fieldSettingsWorkerInstance = new FieldSettingsWorkerFile();
      this.fieldSettingsWorker = wrap(this.fieldSettingsWorkerInstance);
      
      console.log('🏗️ Creating FormulaWorker instance (formula.worker.js)');
      this.formulaWorkerInstance = new FormulaWorkerFile();
      this.formulaWorker = wrap(this.formulaWorkerInstance);
      
      console.log('🏗️ Creating GraphWorker instance (graph.worker.js)');
      this.graphWorkerInstance = new GraphWorkerFile();
      this.graphWorker = wrap(this.graphWorkerInstance);
      
      this.refCount = 1;
      console.log('✅ All shared workers initialized successfully');
      console.log('📊 Workers created: dataParse + fieldSettings + formula + graph');
    } catch (error) {
      console.error('❌ Error initializing shared workers:', error);
      this.initPromise = null;
      throw error;
    }
  }

  async _doInitializeSelective(requiredWorkers) {
    try {
      console.log('🔧 Initializing selective workers...');
      console.log('📁 Loading worker types:', requiredWorkers.join(', '));
      
      const importPromises = [];
      const workerFiles = {};
      
      // Only import the required worker files
      if (requiredWorkers.includes('dataParseWorker')) {
        importPromises.push(
          import("@/workers/dataParse.worker.js").then(m => {
            workerFiles.dataParseWorker = m.default;
          })
        );
      }
      if (requiredWorkers.includes('fieldSettingsWorker')) {
        importPromises.push(
          import("@/workers/fieldSettings.worker.js").then(m => {
            workerFiles.fieldSettingsWorker = m.default;
          })
        );
      }
      if (requiredWorkers.includes('formulaWorker')) {
        importPromises.push(
          import("@/workers/formula.worker.js").then(m => {
            workerFiles.formulaWorker = m.default;
          })
        );
      }
      if (requiredWorkers.includes('graphWorker')) {
        importPromises.push(
          import("@/workers/graph.worker.js").then(m => {
            workerFiles.graphWorker = m.default;
          })
        );
      }
      
      await Promise.all(importPromises);
      
      // Create only the required worker instances
      if (requiredWorkers.includes('dataParseWorker') && !this.dataParseWorker) {
        console.log('🏗️ Creating DataParseWorker instance (dataParse.worker.js)');
        this.dataParseWorkerInstance = new workerFiles.dataParseWorker();
        this.dataParseWorker = wrap(this.dataParseWorkerInstance);
      }
      
      if (requiredWorkers.includes('fieldSettingsWorker') && !this.fieldSettingsWorker) {
        console.log('🏗️ Creating FieldSettingsWorker instance (fieldSettings.worker.js)');
        this.fieldSettingsWorkerInstance = new workerFiles.fieldSettingsWorker();
        this.fieldSettingsWorker = wrap(this.fieldSettingsWorkerInstance);
      }
      
      if (requiredWorkers.includes('formulaWorker') && !this.formulaWorker) {
        console.log('🏗️ Creating FormulaWorker instance (formula.worker.js)');
        this.formulaWorkerInstance = new workerFiles.formulaWorker();
        this.formulaWorker = wrap(this.formulaWorkerInstance);
      }
      
      if (requiredWorkers.includes('graphWorker') && !this.graphWorker) {
        console.log('🏗️ Creating GraphWorker instance (graph.worker.js)');
        this.graphWorkerInstance = new workerFiles.graphWorker();
        this.graphWorker = wrap(this.graphWorkerInstance);
      }
      
      this.refCount = 1;
      console.log('✅ Selective workers initialized successfully');
      console.log('📊 Workers created:', requiredWorkers.join(' + '));
    } catch (error) {
      console.error('❌ Error initializing selective workers:', error);
      this.initPromise = null;
      throw error;
    }
  }

  async getWorkers(requiredWorkers = ['dataParseWorker', 'fieldSettingsWorker', 'formulaWorker', 'graphWorker']) {
    // Cancel any pending termination since a component needs workers
    if (this.terminateTimeout) {
      clearTimeout(this.terminateTimeout);
      this.terminateTimeout = null;
      console.log('🚫 Cancelled worker termination - component requested workers');
    }
    
    // Check if all required workers exist and are valid
    const missingWorkers = requiredWorkers.filter(workerType => {
      const worker = this[workerType];
      return !worker || !this.isWorkerValid(worker);
    });
    
    // If any required workers are missing or invalid, re-initialize them
    if (missingWorkers.length > 0) {
      console.log('🔄 Missing or invalid workers detected:', missingWorkers);
      await this.initializeSelective(requiredWorkers);
    } else {
      this.refCount++;
    }
    
    console.log(`📊 WorkerManager: ${this.refCount} components using shared workers`);
    
    // Return only the requested workers as safe proxies
    const result = {};
    if (requiredWorkers.includes('dataParseWorker')) {
      result.dataParseWorker = this.dataParseWorker ? this.createSafeWorkerProxy(this.dataParseWorker, 'dataParseWorker') : null;
    }
    if (requiredWorkers.includes('fieldSettingsWorker')) {
      result.fieldSettingsWorker = this.fieldSettingsWorker ? this.createSafeWorkerProxy(this.fieldSettingsWorker, 'fieldSettingsWorker') : null;
    }
    if (requiredWorkers.includes('formulaWorker')) {
      result.formulaWorker = this.formulaWorker ? this.createSafeWorkerProxy(this.formulaWorker, 'formulaWorker') : null;
    }
    if (requiredWorkers.includes('graphWorker')) {
      result.graphWorker = this.graphWorker ? this.createSafeWorkerProxy(this.graphWorker, 'graphWorker') : null;
    }
    
    return result;
  }

  async release() {
    if (this.refCount > 0) {
      this.refCount--;
    }
    console.log(`🔄 Worker refCount: ${this.refCount}`);
    
    // Only terminate when no components are using workers and after a delay
    if (this.refCount <= 0) {
      // Clear any existing timeout
      if (this.terminateTimeout) {
        clearTimeout(this.terminateTimeout);
      }
      
      // Set a new timeout for termination
      this.terminateTimeout = setTimeout(async () => {
        if (this.refCount <= 0) {
          console.log('🧹 Terminating workers after delay...');
          await this.terminate();
        }
      }, 2000); // 2 second delay to handle race conditions
    }
  }

  async terminate() {
    if (!this.dataParseWorker && !this.fieldSettingsWorker && !this.formulaWorker && !this.graphWorker) {
      return;
    }

    try {
      console.log('🧹 Terminating all shared workers...');
      
      // Clear worker state before terminating
      if (this.fieldSettingsWorker && this.fieldSettingsWorker.clearWorkerState) {
        await this.fieldSettingsWorker.clearWorkerState();
      }
      
      // Release Comlink proxies
      if (this.dataParseWorker && this.dataParseWorker[releaseProxy]) {
        this.dataParseWorker[releaseProxy]();
      }
      if (this.fieldSettingsWorker && this.fieldSettingsWorker[releaseProxy]) {
        this.fieldSettingsWorker[releaseProxy]();
      }
      if (this.formulaWorker && this.formulaWorker[releaseProxy]) {
        this.formulaWorker[releaseProxy]();
      }
      if (this.graphWorker && this.graphWorker[releaseProxy]) {
        this.graphWorker[releaseProxy]();
      }
      
      // Terminate actual worker instances
      if (this.dataParseWorkerInstance && this.dataParseWorkerInstance.terminate) {
        this.dataParseWorkerInstance.terminate();
      }
      if (this.fieldSettingsWorkerInstance && this.fieldSettingsWorkerInstance.terminate) {
        this.fieldSettingsWorkerInstance.terminate();
      }
      if (this.formulaWorkerInstance && this.formulaWorkerInstance.terminate) {
        this.formulaWorkerInstance.terminate();
      }
      if (this.graphWorkerInstance && this.graphWorkerInstance.terminate) {
        this.graphWorkerInstance.terminate();
      }
      
      // Clean up all references
      this.dataParseWorker = null;
      this.fieldSettingsWorker = null;
      this.formulaWorker = null;
      this.graphWorker = null;
      this.dataParseWorkerInstance = null;
      this.fieldSettingsWorkerInstance = null;
      this.formulaWorkerInstance = null;
      this.graphWorkerInstance = null;
      this.refCount = 0;
      this.initPromise = null;
      
      // Clear any pending termination timeout
      if (this.terminateTimeout) {
        clearTimeout(this.terminateTimeout);
        this.terminateTimeout = null;
      }
      
      console.log('✅ All shared workers terminated');
    } catch (error) {
      console.warn('⚠️ Error terminating shared workers:', error);
    }
  }

  // Check if a single worker is valid (not released)
  isWorkerValid(worker) {
    if (!worker) {
      return false;
    }
    
    try {
      // Try to access a property to check if proxy is still valid
      // This will throw if the proxy has been released
      worker.constructor;
      return true;
    } catch (error) {
      console.warn('⚠️ Worker has been released or is invalid:', error.message);
      return false;
    }
  }

  // Check if workers are still valid (not released)
  areWorkersValid() {
    try {
      // Check if all workers exist and are not released
      const anyWorkerExists = !!(this.dataParseWorker || this.fieldSettingsWorker || this.formulaWorker || this.graphWorker);
      console.log('🔄 Workers exist:', anyWorkerExists);
      if (!anyWorkerExists) {
        return false;
      }
      
      // Try to access a property to check if proxies are still valid
      // This will throw if the proxy has been released
      if (this.dataParseWorker) {
        this.dataParseWorker.constructor;
      }
      if (this.fieldSettingsWorker) {
        this.fieldSettingsWorker.constructor;
      }
      if (this.formulaWorker) {
        this.formulaWorker.constructor;
      }
      if (this.graphWorker) {
        this.graphWorker.constructor;
      }
      
      return true;
    } catch (error) {
      console.warn('⚠️ Workers have been released or are invalid:', error.message);
      // Reset workers if they're invalid
      this.dataParseWorker = null;
      this.fieldSettingsWorker = null;
      this.formulaWorker = null;
      this.graphWorker = null;
      return false;
    }
  }
  
  // Register which workers a page needs
  registerPageRequirements(pageName, requiredWorkers) {
    console.log(`📋 Registering page requirements for ${pageName}:`, requiredWorkers);
    this.pageWorkerRequirements.set(pageName, requiredWorkers);
    this.currentPage = pageName;
  }

  // Get workers needed by current page
  getCurrentPageRequirements() {
    return this.pageWorkerRequirements.get(this.currentPage) || [];
  }

  // Get all workers needed across all pages
  getAllRequiredWorkers() {
    const allRequired = new Set();
    this.pageWorkerRequirements.forEach(workers => {
      workers.forEach(worker => allRequired.add(worker));
    });
    return Array.from(allRequired);
  }

  // Check if a worker is needed by any page
  isWorkerNeeded(workerType) {
    const allRequired = this.getAllRequiredWorkers();
    return allRequired.includes(workerType);
  }

  // Get workers that are not needed by any page
  getUnusedWorkers() {
    const allWorkers = ['dataParseWorker', 'fieldSettingsWorker', 'formulaWorker', 'graphWorker'];
    const allRequired = this.getAllRequiredWorkers();
    return allWorkers.filter(worker => !allRequired.includes(worker));
  }

  // Create a safe proxy that handles released worker errors
  createSafeWorkerProxy(worker, workerName) {
    if (!worker) {
      console.warn(`⚠️ ${workerName} is null, returning null`);
      return null;
    }
    
    return new Proxy(worker, {
      get(target, prop, receiver) {
        try {
          return Reflect.get(target, prop, receiver);
        } catch (error) {
          if (error.message && error.message.includes('Proxy has been released')) {
            console.error(`❌ ${workerName} proxy has been released. Component should re-initialize workers.`);
            // Return a function that throws a more descriptive error
            if (typeof target[prop] === 'function') {
              return async () => {
                throw new Error(`${workerName} has been released. Please re-initialize workers by calling WorkerManager.getWorkers() again.`);
              };
            }
            return null;
          }
          throw error;
        }
      }
    });
  }
  
  // Get safe worker references that won't throw if released
  getSafeWorkers() {
    if (this.areWorkersValid()) {
      return {
        dataParseWorker: this.dataParseWorker,
        fieldSettingsWorker: this.fieldSettingsWorker,
        formulaWorker: this.formulaWorker,
        graphWorker: this.graphWorker
      };
    }
    return null;
  }

  // Force terminate (for debugging/emergency cleanup)
  async forceTerminate() {
    console.log('🚨 Force terminating all workers...');
    this.refCount = 0;
    if (this.terminateTimeout) {
      clearTimeout(this.terminateTimeout);
      this.terminateTimeout = null;
    }
    await this.terminate();
  }

  // Enhanced cleanup for route changes - only unused workers
  async cleanupOnRouteChange() {
    console.log('🔄 Cleaning up unused workers for route change...');
    try {
      // Clear any pending termination timeout
      if (this.terminateTimeout) {
        clearTimeout(this.terminateTimeout);
        this.terminateTimeout = null;
      }
      
      const unusedWorkers = this.getUnusedWorkers();
      console.log('📊 Unused workers to clean up:', unusedWorkers);
      
      if (unusedWorkers.length > 0) {
        await this.selectiveTerminate(unusedWorkers);
        console.log('✅ Unused workers cleaned up for route change');
      } else {
        console.log('ℹ️ No unused workers to clean up');
      }
      
      // Reset initialization promise to allow fresh initialization
      this.initPromise = null;
      this.isInitializing = false;
      
    } catch (error) {
      console.warn('⚠️ Error during selective cleanup:', error);
    }
  }

  // Selectively terminate only specified workers
  async selectiveTerminate(workersToTerminate) {
    console.log('🎯 Selectively terminating workers:', workersToTerminate);
    
    for (const workerType of workersToTerminate) {
      try {
        // Clear worker state if it has a clearWorkerState method
        if (workerType === 'fieldSettingsWorker' && this.fieldSettingsWorker && this.fieldSettingsWorker.clearWorkerState) {
          await this.fieldSettingsWorker.clearWorkerState();
        }
        
        // Release Comlink proxy
        const worker = this[workerType];
        if (worker && worker[releaseProxy]) {
          worker[releaseProxy]();
        }
        
        // Terminate worker instance
        const workerInstance = this[`${workerType}Instance`];
        if (workerInstance && workerInstance.terminate) {
          workerInstance.terminate();
        }
        
        // Clear references
        this[workerType] = null;
        this[`${workerType}Instance`] = null;
        
        console.log(`✅ Terminated ${workerType}`);
      } catch (error) {
        console.warn(`⚠️ Error terminating ${workerType}:`, error.message);
      }
    }
  }
}

// Export singleton instance
const workerManager = new WorkerManager();

// Add global access for debugging
if (typeof window !== 'undefined') {
  window.WorkerManager = workerManager;
}

export default workerManager;
